import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getNamespaceFromDom, runPageInit } from '../barba/page-init-registry';
import { applySubnavForNamespace } from '../widgets/page-subnav/apply-subnav-for-namespace';
import { destroyPermissionRequest } from '../widgets/permission-request';

const EDUCATION_PAGE_SELECTOR = '.education-page';
const TAB_LINK_SELECTOR = 'a.page-tabs__nav-link[href]';

let bound = false;
let navigating = false;

/** Горизонтальный скролл активного таба внутри overflow, без движения страницы. */
export function scrollActiveEducationTabIntoView(root?: ParentNode): void {
  const scope = root ?? document;
  const activeLink = scope.querySelector<HTMLElement>(`${TAB_LINK_SELECTOR}.active`);
  if (!activeLink) return;

  const scroller =
    activeLink.closest<HTMLElement>('.page-tabs__navigation') ??
    activeLink.closest<HTMLElement>('.page-tabs__nav');
  if (!scroller) return;

  requestAnimationFrame(() => {
    const linkRect = activeLink.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const delta = linkRect.left - scrollerRect.left - (scrollerRect.width - linkRect.width) / 2;
    scroller.scrollLeft += delta;
  });
}

function sameUrl(a: string, b: string): boolean {
  try {
    const left = new URL(a, window.location.href);
    const right = new URL(b, window.location.href);
    return (
      left.pathname.replace(/\/+$/, '') === right.pathname.replace(/\/+$/, '') &&
      left.search === right.search
    );
  } catch {
    return a === b;
  }
}

function replaceAfterHero(currentPage: HTMLElement, nextPage: HTMLElement): void {
  const currentHero = currentPage.querySelector('.education-hero');
  const nextHero = nextPage.querySelector('.education-hero');

  if (!currentHero || !nextHero) {
    currentPage.replaceWith(document.importNode(nextPage, true));
    return;
  }

  let node: Element | null = currentHero.nextElementSibling;
  while (node) {
    const next = node.nextElementSibling;
    node.remove();
    node = next;
  }

  const toAppend: Element[] = [];
  let nextNode: Element | null = nextHero.nextElementSibling;
  while (nextNode) {
    toAppend.push(nextNode);
    nextNode = nextNode.nextElementSibling;
  }

  toAppend.forEach((el) => {
    currentPage.appendChild(document.importNode(el, true));
  });

  currentPage.className = nextPage.className;
  const namespace = nextPage.getAttribute('data-barba-namespace');
  if (namespace) {
    currentPage.setAttribute('data-barba-namespace', namespace);
  }
}

async function navigateEducationTab(href: string): Promise<void> {
  if (navigating) return;
  if (sameUrl(href, window.location.href)) return;

  const currentPage = document.querySelector<HTMLElement>(EDUCATION_PAGE_SELECTOR);
  if (!currentPage) return;

  navigating = true;
  const scrollY = window.scrollY || document.documentElement.scrollTop || 0;

  try {
    const response = await fetch(href, {
      credentials: 'same-origin',
    });
    if (!response.ok) {
      window.location.assign(href);
      return;
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextPage = doc.querySelector<HTMLElement>(EDUCATION_PAGE_SELECTOR);
    if (!nextPage) {
      window.location.assign(href);
      return;
    }

    destroyPermissionRequest();
    window.dispatchEvent(
      new CustomEvent('stc:barba-before-leave', {
        detail: { nextNamespace: nextPage.getAttribute('data-barba-namespace') },
      }),
    );

    replaceAfterHero(currentPage, nextPage);

    const nextUrl = response.url || href;
    window.history.pushState({}, '', nextUrl);
    if (doc.title) document.title = doc.title;

    const page = document.querySelector<HTMLElement>(EDUCATION_PAGE_SELECTOR) ?? currentPage;
    const namespace =
      page.getAttribute('data-barba-namespace') ?? getNamespaceFromDom() ?? undefined;

    runPageInit(namespace, page);
    applySubnavForNamespace(namespace);
    ScrollTrigger.refresh();
    window.scrollTo(0, scrollY);
    scrollActiveEducationTabIntoView(page);
    window.dispatchEvent(new Event('stc:barba-after-enter'));
  } catch {
    window.location.assign(href);
  } finally {
    navigating = false;
  }
}

function onEducationTabClick(event: MouseEvent): void {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const link = target.closest<HTMLAnchorElement>(TAB_LINK_SELECTOR);
  if (!link?.closest(EDUCATION_PAGE_SELECTOR)) return;

  const href = link.href;
  if (!href || link.getAttribute('href') === '#') return;

  event.preventDefault();
  event.stopPropagation();
  void navigateEducationTab(href);
}

/** Переключение табов образования без пересоздания hero (без мигания). */
export function initEducationPageTabsNav(): void {
  if (bound) return;
  bound = true;
  document.addEventListener('click', onEducationTabClick, true);
}
